import { Autocomplete, Box, Container, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material';
import FaltaReceberComponent from 'components/FaltaReceberComponent';
import { IAbstractModel } from 'model/abstract.model';
import { ICondicaoPagamento } from 'pages/cadastro-pessoa/pessoa.model';
import { DadosPagamento, IInfoAdicional, TdadosGeneric, TipoInformacaoAdicional } from 'pages/pagamento/pagamento.model';
import PagamentoService from 'pages/pagamento/pagamento.service';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useAppSelector } from 'redux/hooks';
import { currencyOf, deepClone, roundHalfToEven } from 'utils/util';
import { FormaPagamentoDialogContentProps } from '../formapagamento.model';
import LinhasDadosGeneric from '../LinhasDadosGeneric';
import { theme } from 'theme';

const OutrosDialogContent = ({ isNew, error, valorRecebido, uuid, customFaltaReceber }: FormaPagamentoDialogContentProps) => {
    const { hasError, setHasError } = error;
    const { dadosPagamento, setDadosPagamento } = valorRecebido;
    const { faltaReceber } = useAppSelector(s => s.pagamento);
    const dadosPagamentoAsInfoAdicional = dadosPagamento.infoAdicional as IInfoAdicional;
    const { tableRows } = useAppSelector(e => e.pagamento);
    const tipo = TipoInformacaoAdicional.OUTROS;
    const condicoesPagamento = useAppSelector(e => e.preferencias.condicoesPagamento);
    const possuiCondicoesPagamento = !!condicoesPagamento?.length;
    const [filtroCondicaoPagamento, setFiltroCondicaoPagamento] = useState('');
    const [condicaoPagamentoSelecionada, setCondicaoPagamentoSelecionada] = useState<IAbstractModel | null>(!isNew ? dadosPagamentoAsInfoAdicional?.condicaoPagamento ?? null : null);
    const service = PagamentoService();
    const shouldRedistribuirParecelas = useRef(isNew || !dadosPagamentoAsInfoAdicional?.condicaoPagamento);

    const filterCondicoesPagamentos = useCallback((arr: IAbstractModel[], filtro: string) => {
        if (!filtro) return arr;
        return arr.filter(e => e.descritivo.toUpperCase().includes(filtro.toUpperCase()));
    }, []);

    const getValorTotalParcelas = (parcelas: TdadosGeneric[] | undefined) => {
        if (!parcelas?.length) return 0;
        return roundHalfToEven(parcelas?.reduce((acc, curr) => acc + curr.valor, 0) ?? 0, 2);
    };

    useEffect(() => {
        if (!shouldRedistribuirParecelas.current) return;
        shouldRedistribuirParecelas.current = false;

        if (uuid && condicoesPagamento && condicaoPagamentoSelecionada) {
            const parcelas = service.getParcelasCondicaoPagamentoSelecionada(uuid, condicaoPagamentoSelecionada.uuid, dadosPagamento.valorRecebido);
            parcelas.then(response => {
                handleTableRow(response.data.length, response.data);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [condicaoPagamentoSelecionada]);

    const tableRowsValue = useMemo(() => {
        return tableRows.filter(e => !e.pago).reduce((_, act) => _ += act.valor, 0);
    }, [tableRows]);

    useEffect(() => {
        if (dadosPagamento.infoAdicional?.tipo && dadosPagamento.infoAdicional.tipo !== tipo) {
            setDadosPagamento({
                valorRecebido: faltaReceber,
                infoAdicional: undefined
            });
        }

        if (possuiCondicoesPagamento && !dadosPagamento.infoAdicional?.condicaoPagamento) {
            setDadosPagamento(prev => ({
                ...prev,
                infoAdicional: {
                    ...prev.infoAdicional!,
                    dadosGeneric: [],
                    numeroParcelas: 0,
                    observacao: prev.infoAdicional?.observacao ?? null,
                    tipo
                }
            }));
        }

        if (isNew) {
            if (!condicoesPagamento || condicoesPagamento.length === 0) {
                setDadosPagamento(prev => ({
                    ...prev,
                    valorRecebido: faltaReceber - tableRowsValue,
                    infoAdicional: {
                        numeroParcelas: prev.infoAdicional?.numeroParcelas ?? 1,
                        descritivo: prev.infoAdicional?.descritivo ?? getParcelasDescritivo(1, faltaReceber - tableRowsValue),
                        tipo,
                        dadosGeneric: [
                            {
                                valor: faltaReceber - tableRowsValue,
                                vencimento: new Date().toISOString(),
                                vencimentoDisplay: new Date().toISOString()
                            }
                        ],
                    }
                }));
            } else {
                setDadosPagamento(prev => {
                    return {
                        ...prev,
                        valorRecebido: faltaReceber,
                        infoAdicional: {
                            ...prev.infoAdicional!,
                            tipo,
                            descritivo: prev.infoAdicional?.descritivo ?? '',
                            numeroParcelas: prev.infoAdicional?.numeroParcelas ?? 1,
                            dadosGeneric: []
                        }
                    };
                });
            }
        } else {
            if (!condicoesPagamento || condicoesPagamento.length === 0) {
                setDadosPagamento(dadosPagamento);
            } else {
                setDadosPagamento(prev => {
                    return {
                        ...prev,
                        valorRecebido: getValorTotalParcelas(prev.infoAdicional?.dadosGeneric),
                        infoAdicional: {
                            ...prev.infoAdicional!,
                            tipo,
                            descritivo: prev.infoAdicional?.descritivo ?? '',
                            numeroParcelas: prev.infoAdicional?.numeroParcelas ?? 1,
                            dadosGeneric: prev.infoAdicional?.dadosGeneric ?? []
                        }
                    };
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNew]);

    useEffect(() => {
        setDadosPagamento(prev => ({
            ...prev,
            infoAdicional: {
                ...prev.infoAdicional!,
                descritivo: getParcelasDescritivo(prev.infoAdicional?.numeroParcelas ?? 1, prev.valorRecebido)
            }
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dadosPagamento.valorRecebido]);

    const onChangeValorRecebido = (value: number = 0) => {
        if (value > faltaReceber) {
            setHasError(() => true);
        } else {
            setHasError(() => false);
        }

        setDadosPagamento(prev => ({ ...prev, valorRecebido: value }));

        if (!condicaoPagamentoSelecionada) {
            setDadosPagamento(prev => {
                const numeroParcelas = prev.infoAdicional?.numeroParcelas ?? 1;
                return {
                    ...prev,
                    infoAdicional: {
                        ...prev.infoAdicional!,
                        dadosGeneric: undefined,
                        descritivo: getParcelasDescritivo(numeroParcelas, value),
                        numeroParcelas,
                        tipo
                    }
                };
            });
        } else {
            if (!shouldRedistribuirParecelas.current) {
                shouldRedistribuirParecelas.current = false;
                return;
            }
            setDadosPagamento(prev => ({
                ...prev,
                infoAdicional: {
                    ...prev.infoAdicional!,
                    dadosGeneric: [],
                    descritivo: getParcelasDescritivo(1, value),
                    numeroParcelas: 1,
                    tipo
                }
            }));
            service.getParcelasCondicaoPagamentoSelecionada(uuid!, condicaoPagamentoSelecionada.uuid, value).then(response => {
                handleTableRow(response.data.length, response.data);
            });
        }

    };

    const onChangePrazos = (e: SelectChangeEvent<number>, child: ReactNode) => {
        const p = (child as any).props;
        const parcelas = p.value as number;
        const valorTotalCentavos = Math.round(dadosPagamento.valorRecebido * 100);
        const sobraCentavos = valorTotalCentavos % parcelas;
        const valorRateadoCentavos = (valorTotalCentavos - sobraCentavos) / parcelas;
        const ultimaParcelaCentavos = valorRateadoCentavos + sobraCentavos;
        const dataBase = dadosPagamento.infoAdicional?.dadosGeneric?.[0]?.vencimento ?? new Date().toISOString();
        const novosDadosGeneric = Array.from({ length: parcelas }, (_, index) => {
            const data = new Date(dataBase);
            data.setMonth(data.getMonth() + index);
            const iso = data.toISOString();
            return {
                valor: (index === parcelas - 1 ? ultimaParcelaCentavos : valorRateadoCentavos) / 100,
                vencimento: iso,
                vencimentoDisplay: iso,
            };
        });
        setDadosPagamento(prev => ({
            ...prev,
            infoAdicional: {
                descritivo: p.children,
                numeroParcelas: parcelas,
                tipo,
                dadosGeneric: novosDadosGeneric,
            },
        }));
    };

    const getParcelasValue = useCallback((dadosPagamento: DadosPagamento): number | '' => {
        return dadosPagamento.infoAdicional?.numeroParcelas ?? 1;
    }, []);

    const getParcelasDescritivo = useCallback((parcela: number, valorRecebido: number) => {
        return `${parcela}x - ${currencyOf(valorRecebido / parcela)}`;
    }, []);

    const handleTableRow = (numeroParcela: number, dadosParcela?: TdadosGeneric[]) => {
        const dadosGeneric = (dadosPagamentoAsInfoAdicional)?.dadosGeneric ? [...(dadosPagamentoAsInfoAdicional)?.dadosGeneric] : [];

        if (dadosGeneric.length > numeroParcela && numeroParcela > 0) {
            const parcelasExtra = dadosGeneric.length - numeroParcela;
            for (let i = 0; i < parcelasExtra; i++) {
                dadosGeneric.pop();
            }
        }

        if (dadosParcela) {
            if (!!condicaoPagamentoSelecionada) dadosGeneric.length = 0;
            for (const parcela of dadosParcela) {
                const date = new Date(parcela.vencimento);
                dadosGeneric.push({
                    valor: parcela.valor,
                    vencimento: date.toISOString(),
                    vencimentoDisplay: date.toISOString()
                });
            }
        }

        setDadosPagamento(prevDadosPagamento => ({
            ...prevDadosPagamento,
            infoAdicional: {
                ...prevDadosPagamento.infoAdicional!,
                dadosGeneric: dadosGeneric ?? [],
                numeroParcelas: numeroParcela,
                descritivo: getParcelasDescritivo(numeroParcela, prevDadosPagamento.valorRecebido),
                observacao: prevDadosPagamento.infoAdicional?.observacao ?? null
            },
        }));
    };

    const handleChangeValor = (index: number, valor: number) => {
        setDadosPagamento(prev => {
            if (!prev.infoAdicional) return prev;
            const dadosGeneric = deepClone(prev.infoAdicional.dadosGeneric);
            const abc = dadosGeneric?.[index];
            if (abc) {
                abc.valor = valor;
            }
            const novoValorTotal = getValorTotalParcelas(dadosGeneric);
            setHasError(() => novoValorTotal > faltaReceber);
            shouldRedistribuirParecelas.current = false;
            return {
                ...prev,
                valorRecebido: novoValorTotal,
                infoAdicional: {
                    ...prev.infoAdicional!,
                    dadosGeneric: dadosGeneric ?? [],
                },
            };
        });
    };

    return (
        <>
            <Box sx={{
                display: 'flex',
                flexDirection: possuiCondicoesPagamento ? 'row' : 'column',
                columnGap: '15px',
            }}>
                <FormControl
                    fullWidth
                    variant="standard"
                    sx={{
                        width: '15rem',
                        paddingTop: '1px'
                    }}
                    error={hasError}
                >
                    <NumericFormat
                        id="outros_div"
                        value={dadosPagamento.valorRecebido}
                        onValueChange={({ floatValue }, { source }) => {
                            shouldRedistribuirParecelas.current = source === 'event';
                            onChangeValorRecebido(floatValue);
                        }}
                        error={hasError}
                        decimalScale={2}
                        fixedDecimalScale
                        allowedDecimalSeparators={[',']}
                        customInput={TextField}
                        decimalSeparator=","
                        prefix={'R$ '}
                        thousandsGroupStyle="thousand"
                        thousandSeparator="."
                        variant="standard"
                        allowNegative={false}
                        inputProps={{
                            id: "outros_input_value",
                            style: {
                                textAlign: 'center',
                                fontSize: '24px',
                                color: hasError ? 'var(--error)' : theme.palette.primary.main,
                                fontWeight: 700,
                                fontFamily: 'Work Sans, sans-serif'
                            }
                        }}
                    />
                    {hasError &&
                        <FormHelperText
                            sx={{
                                whiteSpace: 'nowrap'
                            }}
                        >
                            O valor não pode ser maior que a venda :(
                        </FormHelperText>}
                    {!hasError && <FormHelperText> </FormHelperText>}
                </FormControl>

                <FormControl
                    fullWidth
                    variant="standard"
                    sx={{ width: '15rem', transform: possuiCondicoesPagamento ? 'translateY(-3px)' : 'unset' }}
                    error={false}
                >
                    {(!condicoesPagamento || condicoesPagamento.length === 0) && <>
                        <InputLabel id="prazos-label">Parcelas</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={getParcelasValue(dadosPagamento)}
                            onChange={onChangePrazos}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 48 * 4.5 + 8,
                                        width: 100
                                    },
                                },
                            }}
                        >
                            {Array.from([].constructor(12)).map((s, index) => {
                                const parcela = ++index;
                                return (
                                    <MenuItem
                                        id={`menu_item_outros_${index}`}
                                        key={`parcela_${parcela}`}
                                        value={parcela}
                                    >
                                        {getParcelasDescritivo(parcela, dadosPagamento.valorRecebido)}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </>}

                    {condicoesPagamento && condicoesPagamento.length > 0 &&
                        <Autocomplete
                            value={condicaoPagamentoSelecionada}
                            id="ac-condicao-pagamento-outros"
                            options={filterCondicoesPagamentos(condicoesPagamento, filtroCondicaoPagamento)}
                            getOptionLabel={(option) => option.descritivo}
                            onInputChange={(_, value) => { setFiltroCondicaoPagamento(value); }}
                            filterOptions={(x) => x}
                            noOptionsText={'Nenhum resultado encontrado'}
                            onChange={(_, condicao) => {
                                shouldRedistribuirParecelas.current = true;
                                setCondicaoPagamentoSelecionada(condicao);
                                setDadosPagamento((prevDadosPagamento) => ({
                                    ...prevDadosPagamento,
                                    infoAdicional: {
                                        ...prevDadosPagamento.infoAdicional!,
                                        condicaoPagamento: condicao,
                                        dadosGeneric: [],
                                        numeroParcelas: condicao ? (condicao as ICondicaoPagamento).numeroParcelas : 1,
                                        observacao: prevDadosPagamento.infoAdicional?.observacao ?? null,
                                        tipo: tipo
                                    },
                                }));
                            }}
                            isOptionEqualToValue={(option, value) => {
                                if (!option || !value) return false;
                                return option.uuid === value.uuid;
                            }}
                            renderInput={
                                (params) => <TextField
                                    {...params}
                                    label="Condição de pagamento *"
                                    variant="standard"
                                />}
                        />
                    }
                </FormControl>
            </Box>


            {condicoesPagamento && condicoesPagamento.length > 0 && <Grid container >
                <TableContainer
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 1,
                        position: 'relative',
                        boxSizing: 'border-box',
                        height: '100%',
                        maxHeight: '290px',
                        minHeight: '290px',
                    }}
                    className="color-orange"
                >
                    <Table
                        id="content-table"
                        stickyHeader
                        sx={{
                            borderRadius: 1,
                        }}
                        size="small"
                    >

                        <TableHead>
                            <TableRow >
                                <TableCell sx={{ width: '3rem' }} align="center">
                                    Valor
                                </TableCell>

                                <TableCell sx={{ width: '3rem' }} align="center">
                                    Vencimento
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {dadosPagamento.infoAdicional && (dadosPagamentoAsInfoAdicional).dadosGeneric?.map((row, index) => (
                                <LinhasDadosGeneric
                                    row={row}
                                    index={index}
                                    handleChangeValor={handleChangeValor}
                                    handleDatavencimento={() => { }}
                                    hasError={hasError}
                                    key={`row-${index}`}
                                    possuiCondicaoPagamento={true}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Container>
                    {hasError && <FormHelperText sx={{ textAlign: 'center', fontWeight: 600, fontSize: 15, color: theme.palette.error.main }}>O valor não pode ser maior que a venda :(</FormHelperText>}
                    {!hasError && <FormHelperText sx={{ textAlign: 'center', fontWeight: 600, fontSize: 15 }}> </FormHelperText>}
                </Container>
            </Grid>
            }

            <FaltaReceberComponent
                valorRecebido={dadosPagamento.valorRecebido}
                tableRowsValue={tableRowsValue}
            />
        </>
    );
};

export default OutrosDialogContent;
